import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { fmtHorodatage } from '@/lib/lmdsJours';

const fmtVal = (p, v) => {
  if (p.type === 'booleen') return v ? 'Oui' : 'Non';
  if (p.type === 'montant') return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(v || 0);
  if (p.type === 'pourcentage') return `${v} %`;
  return String(v);
};

// 🧾 Phase 4A : une carte par paramètre (valeur éditable selon le type, rétablir, dernière modification, historique dépliable, surcharges par année).
export function CarteParametre({ p, onEnregistrer, onRetablir, onSurcharge }) {
  const [v, setV] = useState(p.valeur);
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [vAnnee, setVAnnee] = useState('');
  const [hist, setHist] = useState(false);
  const modifie = String(v) !== String(p.valeur);
  const champ = () => {
    if (p.lecture_seule) return <span className="font-semibold" data-testid={`param-valeur-${p.cle}`}>{fmtVal(p, p.valeur)}</span>;
    if (p.type === 'booleen') return <input type="checkbox" checked={!!v} onChange={(e) => setV(e.target.checked)} data-testid={`param-input-${p.cle}`} />;
    if (p.type === 'choix') return (
      <select className="border rounded-md h-9 px-2 text-sm bg-white" value={v} onChange={(e) => setV(e.target.value)} data-testid={`param-input-${p.cle}`}>
        {p.choix.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>);
    if (p.type === 'texte') return <textarea className="w-full border rounded-md p-2 text-sm" rows={2} value={v} onChange={(e) => setV(e.target.value)} data-testid={`param-input-${p.cle}`} />;
    return <Input type="number" step={p.type === 'entier' ? 1 : 'any'} value={v} onChange={(e) => setV(e.target.value)} className="w-40" data-testid={`param-input-${p.cle}`} />;
  };
  return (
    <Card data-testid={`param-${p.cle}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">{p.libelle}
          {p.lecture_seule && <Badge variant="secondary" data-testid={`param-lecture-seule-${p.cle}`}>{p.cle === 'deductions_occurrences_entieres' ? 'règle confirmée' : p.cle.startsWith('t') && p.cle.endsWith('_pct') ? 'taux légal — équipe technique' : 'lecture seule'}</Badge>}
          {!p.est_defaut && !p.lecture_seule && <Badge variant="outline">modifié</Badge>}
        </CardTitle>
        {p.description && <p className="text-xs text-slate-500">{p.description}</p>}
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          {champ()}
          {!p.lecture_seule && (
            <>
              <Button size="sm" disabled={!modifie} onClick={() => onEnregistrer(p.cle, p.type === 'booleen' ? !!v : v)} data-testid={`param-enregistrer-${p.cle}`}>Enregistrer</Button>
              <Button size="sm" variant="outline" disabled={p.est_defaut} onClick={() => onRetablir(p.cle).then(() => setV(p.valeur_par_defaut))} data-testid={`param-retablir-${p.cle}`}>Rétablir la valeur par défaut</Button>
            </>
          )}
          <span className="text-xs text-slate-400">défaut : {fmtVal(p, p.valeur_par_defaut)}</span>
        </div>
        {p.annuel && (
          <div className="border-t pt-2 space-y-1" data-testid={`param-surcharges-${p.cle}`}>
            <p className="text-xs font-medium">Objectif par année</p>
            {Object.entries(p.par_annee).map(([a, val]) => (
              <div key={a} className="flex items-center gap-2 text-xs" data-testid={`param-surcharge-${p.cle}-${a}`}>
                <span>{a} : <b>{fmtVal(p, val)}</b></span>
                <Button size="sm" variant="ghost" className="h-6 text-red-700" onClick={() => onSurcharge(p.cle, Number(a), null)} data-testid={`param-surcharge-retirer-${p.cle}-${a}`}>Retirer</Button>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <Input type="number" className="w-24 h-8" value={annee} onChange={(e) => setAnnee(parseInt(e.target.value) || annee)} data-testid={`param-surcharge-annee-${p.cle}`} />
              <Input type="number" className="w-36 h-8" placeholder="Valeur" value={vAnnee} onChange={(e) => setVAnnee(e.target.value)} data-testid={`param-surcharge-valeur-${p.cle}`} />
              <Button size="sm" variant="outline" className="h-8" disabled={!vAnnee} onClick={() => onSurcharge(p.cle, annee, vAnnee).then((ok) => ok && setVAnnee(''))} data-testid={`param-surcharge-ajouter-${p.cle}`}>Ajouter la surcharge</Button>
            </div>
          </div>
        )}
        <p className="text-xs text-slate-500" data-testid={`param-modif-${p.cle}`}>
          {p.modifie_le ? `Dernière modification par ${p.modifie_par || '—'} le ${fmtHorodatage(p.modifie_le)}` : 'Jamais modifié (valeur par défaut)'}
          {p.historique.length > 0 && <button className="ml-2 underline" onClick={() => setHist(!hist)} data-testid={`param-historique-toggle-${p.cle}`}>{hist ? 'Masquer' : 'Historique'} ({p.historique.length})</button>}
        </p>
        {hist && (
          <ul className="text-xs text-slate-600 space-y-0.5" data-testid={`param-historique-${p.cle}`}>
            {[...p.historique].reverse().map((h, i) => <li key={i}>{fmtHorodatage(h.date)} — {h.portee} : {String(h.ancienne)} → {String(h.nouvelle)} ({h.par || '—'})</li>)}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
